import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  DEFAULT_CURRENCY_CODE,
  INCLUDE_HIDDEN_BRANCHES_PARAM
} from '@constants/common';
import { AccountType, PAGINATION_LIMIT } from '@constants/generic';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { useLazyGetAllBranchesQuery } from '@features/branch-management/queries';
import { formatGroupData } from '@features/group-management/helper';
import {
  useLazyGetAllGroupsWithoutPaginationQuery,
  useLazyGetGroupByIdQuery
} from '@features/group-management/queries/GroupQuery';
import useNotify from '@hooks/notify';
import {
  FeeConfigurationType,
  FormDataType,
  LeadBranchGroupDetailsData,
  TermsList,
  ValidationError
} from '@types';
import { formatISODate } from '@utils/date';
import {
  getAmountErrorMessage,
  getMaxAmountErrorMessage,
  sanitizeNumberInput
} from '@utils/generic';
import { buildQueryString } from '@utils/queryStringBuilder';
import { getLocalStorageItem } from '@utils/storage';
import { ApiError } from 'types/api';
import { Branch } from 'types/branches';
import { DropdownOption } from 'types/dropdown';
import { Group } from 'types/groups';

import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT,
  SPLIT_REQUIRED_MIN_AMOUNT
} from '../constants/common';
import {
  buildEditFormData,
  buildPaymentSchedule,
  getDefaultBranchMember,
  getDefaultGroupMemberBody,
  getResponseValidationError,
  processTermsList
} from '../helper';
import {
  useGetLeadDetailsQuery,
  useLazyLeadMemberFeeCollectionSettingsQuery,
  useLeadAddToBranchGroupDetailsMutation,
  useLeadAddToBranchMutation,
  useLeadAddToGroupMutation,
  useLeadEditMemberMutation
} from '../queries/LeadManagementQuery';
import {
  LeadAddToBranchRequest,
  LeadAddToGroupRequest
} from '../types/request';

const useAddToBranchGroup = (leadId?: string) => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { accountType, currencyConfig, currentOrg } = useSelector(
    (state: any) => state.user
  );
  const { createErrorAlert, createSuccessAlert } = useNotify();

  const [branchesList, setBranchesList] = useState<Branch[]>([]);
  const [groupsList, setGroupsList] = useState<Group[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<DropdownOption | null>(
    null
  );
  const [selectedGroup, setSelectedGroup] = useState<DropdownOption | null>(
    null
  );
  const [groupFormData, setGroupFormData] = useState<FormDataType | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [formError, setFormError] = useState<ValidationError>({});
  const [leadBranchGroupDetailsData, setLeadBranchGroupDetailsData] = useState<
    LeadBranchGroupDetailsData[]
  >([]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editMemberId, setEditMemberId] = useState<string | null>(null);
  const [isBranchOnlyEdit, setIsBranchOnlyEdit] = useState(false);
  const [isEditDataLoading, setIsEditDataLoading] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingForceCreateAction, setPendingForceCreateAction] = useState<
    (() => Promise<void>) | null
  >(null);
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [feeCategories, setFeeCategories] = useState<FeeConfigurationType[]>(
    []
  );
  const [showFeeConfigModal, setShowFeeConfigModal] = useState(false);
  const [newFeeData, setNewFeeData] = useState<FeeConfigurationType[]>([]);

  const [getAllBranches] = useLazyGetAllBranchesQuery();
  const [getAllGroups] = useLazyGetAllGroupsWithoutPaginationQuery();
  const [getGroupById, { isLoading: isGroupDetailsLoading }] =
    useLazyGetGroupByIdQuery();
  const [leadAddToBranch, { isLoading: isLeadAddToBranchLoading }] =
    useLeadAddToBranchMutation();
  const [leadAddToGroup, { isLoading: isLeadAddToGroupLoading }] =
    useLeadAddToGroupMutation();
  const [leadAddToBranchGroupDetails] =
    useLeadAddToBranchGroupDetailsMutation();
  const [leadEditMember, { isLoading: isLeadEditMemberLoading }] =
    useLeadEditMemberMutation();
  const [leadMemberFeeCollectionSettings] =
    useLazyLeadMemberFeeCollectionSettingsQuery();

  const { data: leadDetails } = useGetLeadDetailsQuery(
    { organizationId: organizationId || '', leadId: leadId || '' },
    { skip: !organizationId || !leadId }
  );

  const leadName = leadDetails?.name || '';
  const leadCountryCode = leadDetails?.country_code || '+91';
  const leadMobileNumber = leadDetails?.mobile_number || '';

  const isSubmitting =
    isLeadAddToBranchLoading ||
    isLeadAddToGroupLoading ||
    isLeadEditMemberLoading;

  const handleGetAllBranches = async () => {
    const res = await getAllBranches({
      organizationId,
      queryString: `?currency_code=${DEFAULT_CURRENCY_CODE}&${INCLUDE_HIDDEN_BRANCHES_PARAM}`
    });
    if (res?.data) {
      setBranchesList(res.data?.data);
    }
  };

  const handleGetAllGroups = async (branchId: string) => {
    if (!branchId) return;
    const res = await getAllGroups({
      params: { organizationId, branchId },
      queryString: '?status=active'
    });
    if (res.data) {
      const groups = Array.isArray(res.data) ? res.data : res.data.data || [];
      setGroupsList(groups);
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(error.data?.message || 'Error in fetching groups');
    }
  };

  const handleGetGroupById = async (branchId: string, groupId: string) => {
    if (!branchId || !groupId) return;
    const queryString = buildQueryString({
      include_payment_stats: true,
      include_group_stats: true,
      include_payment_details: true,
      include_fee_categories: true
    });
    const res = await getGroupById({
      params: { organizationId, branchId, groupId },
      queryString
    });
    if (res.data) {
      const formattedData = formatGroupData(res.data);
      const groupFeeConfig = res.data.group_fee_amount_configuration;
      if (
        res.data.is_fee_configured &&
        groupFeeConfig?.length &&
        formattedData.isCustomTerm &&
        formattedData.termsList?.length
      ) {
        formattedData.termsList = formattedData.termsList.map(
          (term: TermsList) => ({
            ...term,
            feeCategoryData:
              term.feeCategoryData && term.feeCategoryData.length > 0
                ? term.feeCategoryData
                : groupFeeConfig
          })
        );
        setFeeCategories(groupFeeConfig);
      } else if (res.data.is_fee_configured && groupFeeConfig?.length) {
        setFeeCategories(groupFeeConfig);
      } else {
        setFeeCategories([]);
      }
      setGroupFormData(formattedData);
      setStartDate(formattedData.startDate || '');
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(
        error.data?.message || 'Error in fetching group details'
      );
    }
  };

  const handleFormInputChange = (fieldName: string, value: any) => {
    setGroupFormData((prev) => (prev ? { ...prev, [fieldName]: value } : prev));
  };

  const handleAddModalOpen = () => {
    sendMixpanelEvent(Event.LEAD_ADD_TO_GROUP_CLICKED, {
      org_id: organizationId,
      org_name: currentOrg?.name,
      lead_id: leadId,
      lead_status: leadDetails?.status?.value,
      priority: leadDetails?.priority,
      lead_category: leadDetails?.category?.value,
      lead_source: leadDetails?.source,
      lead_phone_number: leadDetails?.mobile_number,
      amount: (leadDetails?.deal_value ?? 0) / 100
    });

    setIsEditMode(false);
    setEditMemberId(null);
    setSelectedBranch(null);
    setSelectedGroup(null);
    setGroupFormData(null);
    setStartDate('');
    setFormError({});
    setFeeCategories([]);
    setIsOpen(true);
  };

  const handleClearGroup = () => {
    setSelectedGroup(null);
    setGroupFormData(null);
    setFeeCategories([]);
  };

  const handleModalClose = () => {
    setIsOpen(false);
    setIsEditMode(false);
    setEditMemberId(null);
    setIsBranchOnlyEdit(false);
    setSelectedBranch(null);
    setSelectedGroup(null);
    setGroupFormData(null);
    setStartDate('');
    setFormError({});
    setFeeCategories([]);
  };

  const fetchBranchGroupDetails = async () => {
    if (!leadName || !leadMobileNumber) return;
    setIsTableLoading(true);
    const res = await leadAddToBranchGroupDetails({
      organizationId,
      body: {
        name: leadName,
        mobile_number: leadMobileNumber,
        country_code: leadCountryCode.replace('+', '')
      }
    });
    if ('data' in res) {
      setLeadBranchGroupDetailsData(
        (res.data?.data as LeadBranchGroupDetailsData[]) || []
      );
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(
        error.data?.message || 'Error in fetching lead branch group details'
      );
    }
    setIsTableLoading(false);
  };

  const isFeeConfigured =
    feeCategories.length > 0 || !!groupFormData?.feeCategoryEnabled;

  const getMemberBody = () => ({
    ...getDefaultGroupMemberBody(
      leadName,
      leadMobileNumber,
      leadCountryCode,
      groupFormData?.amount || '',
      buildPaymentSchedule(groupFormData, startDate)
    ),
    ...(isFeeConfigured && {
      member_fee_configuration: feeCategories.map((item) => ({
        branch_fee_category_id: item.branch_fee_category_id,
        amount: item.amount
      }))
    })
  });

  const handleForceCreateAddToGroup = async () => {
    if (!selectedBranch?.id || !selectedGroup?.id || !groupFormData) return;

    const payload: LeadAddToGroupRequest = {
      params: {
        organizationId,
        branchId: selectedBranch.id as string,
        groupId: selectedGroup.id as string,
        leadId: leadId as string
      },
      body: { members_added: [getMemberBody()], force_create: true }
    };

    const res = await leadAddToGroup(payload);
    if ('data' in res) {
      const validationError = getResponseValidationError(res.data);
      if (validationError) {
        createErrorAlert(validationError);
        return;
      }

      sendMixpanelEvent(Event.LEAD_ADDED_TO_GROUPS, {
        org_id: organizationId,
        org_name: currentOrg?.name,
        lead_id: leadId,
        group_id: selectedGroup.id,
        group_name: selectedGroup.value,
        group_amount: groupFormData?.amount,
        lead_status: leadDetails?.status?.value,
        lead_priority: leadDetails?.priority,
        lead_source: leadDetails?.source,
        lead_deal_value: (leadDetails?.deal_value ?? 0) / 100,
        lead_category: leadDetails?.category?.value,
        leads_phone_number: leadDetails?.mobile_number,
        lead_location: `${leadDetails?.country}, ${leadDetails?.state}, ${leadDetails?.city}`
      });

      createSuccessAlert('Lead added to group successfully');
      setShowDuplicateModal(false);
      setPendingForceCreateAction(null);
      handleModalClose();
      fetchBranchGroupDetails();
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(error.data?.message || 'Error in adding lead to group');
    }
  };

  const handleForceCreateAddToBranch = async () => {
    if (!selectedBranch?.id) return;

    const payload: LeadAddToBranchRequest = {
      params: {
        organizationId,
        branchId: selectedBranch.id as string,
        leadId: leadId as string
      },
      body: {
        branch_members: [
          getDefaultBranchMember(leadName, leadMobileNumber, leadCountryCode)
        ],
        force_create: true
      }
    };

    const res = await leadAddToBranch(payload);
    if ('data' in res) {
      const validationError = getResponseValidationError(res.data);
      if (validationError) {
        createErrorAlert(validationError);
        return;
      }
      createSuccessAlert('Lead added to branch successfully');
      setShowDuplicateModal(false);
      setPendingForceCreateAction(null);
      handleModalClose();
      fetchBranchGroupDetails();
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(error.data?.message || 'Error in adding lead to branch');
    }
  };

  const handleDuplicateModalCancel = () => {
    setShowDuplicateModal(false);
    setPendingForceCreateAction(null);
  };

  const handleDuplicateModalProceed = () => {
    if (pendingForceCreateAction) pendingForceCreateAction();
  };

  const handleAddToBranchGroup = async () => {
    if (!selectedBranch?.id || !leadName || !leadMobileNumber) {
      createErrorAlert('Please fill in all required fields');
      return;
    }

    if (selectedGroup?.id && groupFormData) {
      const paymentSchedule = buildPaymentSchedule(groupFormData, startDate);

      if (isEditMode && editMemberId && !isBranchOnlyEdit) {
        const editMemberBody = {
          ...getMemberBody(),
          ...(paymentSchedule && { payment_schedule: paymentSchedule }),
          skip_current_interval: groupFormData?.dueDateConsent === 'next'
        };

        const res = await leadEditMember({
          organizationId,
          leadId: leadId as string,
          branchId: selectedBranch.id as string,
          groupId: selectedGroup.id as string,
          memberId: editMemberId,
          body: editMemberBody
        });
        if ('data' in res) {
          const validationError = getResponseValidationError(res.data);
          if (validationError) {
            createErrorAlert(validationError);
            return;
          }
          createSuccessAlert('Lead updated successfully');
          handleModalClose();
          fetchBranchGroupDetails();
        } else if ('error' in res) {
          const error = res.error as ApiError;
          createErrorAlert(
            error.data?.message || 'Error in updating lead member'
          );
        }
        return;
      }

      const payload: LeadAddToGroupRequest = {
        params: {
          organizationId,
          branchId: selectedBranch.id as string,
          groupId: selectedGroup.id as string,
          leadId: leadId as string
        },
        body: { members_added: [getMemberBody()], force_create: false }
      };

      const res = await leadAddToGroup(payload);
      if ('data' in res) {
        const validationError = getResponseValidationError(res.data);
        if (validationError) {
          createErrorAlert(validationError);
          return;
        }
        const responseData = res.data?.data as any;
        if (responseData?.warning_records?.length > 0) {
          setPendingForceCreateAction(() => handleForceCreateAddToGroup);
          setShowDuplicateModal(true);
        } else {
          await handleForceCreateAddToGroup();
        }
      } else if ('error' in res) {
        const error = res.error as ApiError;
        createErrorAlert(
          error.data?.message || 'Error in adding lead to group'
        );
      }
      return;
    }

    const payload: LeadAddToBranchRequest = {
      params: {
        organizationId,
        branchId: selectedBranch.id as string,
        leadId: leadId as string
      },
      body: {
        branch_members: [
          getDefaultBranchMember(leadName, leadMobileNumber, leadCountryCode)
        ],
        force_create: false
      }
    };

    const res = await leadAddToBranch(payload);
    if ('data' in res) {
      const validationError = getResponseValidationError(res.data);
      if (validationError) {
        createErrorAlert(validationError);
        return;
      }
      const responseData = res.data?.data as any;
      if (responseData?.warning_records?.length > 0) {
        setPendingForceCreateAction(() => handleForceCreateAddToBranch);
        setShowDuplicateModal(true);
      } else {
        await handleForceCreateAddToBranch();
      }
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(error.data?.message || 'Error in adding lead to branch');
    }
  };

  const branchListOptions: DropdownOption[] = (branchesList?.map(
    (branch: Branch) => ({
      id: branch.id,
      value: branch.name,
      label: branch.name
    })
  ) || []) as DropdownOption[];

  const groupListOptions: DropdownOption[] = (groupsList?.map(
    (group: Group) => ({
      id: group.id,
      value: group.name,
      label: group.name
    })
  ) || []) as DropdownOption[];

  const onAmountChange = (value: string) => {
    setFormError((prev) => ({ ...prev, amount: '' }));
    const sanitizedAmount = sanitizeNumberInput(value);
    if (Number(sanitizedAmount) > GROUP_MEMBER_MAX_AMOUNT) {
      setFormError((prev) => ({
        ...prev,
        amount: `Amount cannot be greater than ${GROUP_MEMBER_MAX_AMOUNT}`
      }));
    }
    handleFormInputChange('amount', sanitizedAmount);
    if (
      Number(sanitizedAmount) < SPLIT_REQUIRED_MIN_AMOUNT &&
      groupFormData?.paymentInstallment
    )
      handleFormInputChange('paymentInstallment', false);
  };

  const onAmountBlur = (value: string) => {
    if (value.length && Number(value) < GROUP_MEMBER_MIN_AMOUNT)
      setFormError((prev) => ({
        ...prev,
        amount: getAmountErrorMessage(GROUP_MEMBER_MIN_AMOUNT)
      }));
    if (value.length && Number(value) > GROUP_MEMBER_MAX_AMOUNT)
      setFormError((prev) => ({
        ...prev,
        amount: getMaxAmountErrorMessage(GROUP_MEMBER_MAX_AMOUNT)
      }));
  };

  const updateTermsList = (termsList: TermsList[]) => {
    const lastTerm = termsList[termsList.length - 1];
    const lastTermRefDate = lastTerm?.dueDate;
    if (
      termsList.length > 0 &&
      groupFormData?.endDate &&
      lastTermRefDate &&
      new Date(new Date(lastTermRefDate).setHours(0, 0, 0, 0)) <=
        new Date(new Date(groupFormData.endDate).setHours(0, 0, 0, 0))
    ) {
      setFormError((prev) => ({ ...prev, endDate: '' }));
    }
    handleFormInputChange('termsList', termsList);
  };

  const handleAmountClick = (e: React.MouseEvent) => {
    if (isFeeConfigured && !groupFormData?.isCustomTerm) {
      e.stopPropagation();
      e.preventDefault();
      setNewFeeData([...feeCategories]);
      setShowFeeConfigModal(true);
    }
  };

  const onCategoryAmountChange = (categoryName: string, value: string) => {
    setNewFeeData((prev) =>
      prev.map((cat) =>
        cat.category === categoryName ? { ...cat, amount: value } : cat
      )
    );
  };

  const handleFeeConfigSave = () => {
    setFeeCategories(newFeeData);
    const totalAmount =
      newFeeData?.reduce((sum, cat) => sum + (Number(cat.amount) || 0), 0) || 0;
    handleFormInputChange('amount', totalAmount.toString());
    setShowFeeConfigModal(false);
  };

  const handleFeeConfigCancel = () => {
    setNewFeeData([...feeCategories]);
    setShowFeeConfigModal(false);
  };

  const formattedFeeCategories = newFeeData.map(({ category, amount }) => ({
    category,
    amount
  }));

  const totalFeeConfigAmount =
    newFeeData?.reduce((sum, cat) => sum + (Number(cat.amount) || 0), 0) || 0;

  const isFeeConfigSaveDisabled =
    totalFeeConfigAmount < GROUP_MEMBER_MIN_AMOUNT ||
    totalFeeConfigAmount > GROUP_MEMBER_MAX_AMOUNT ||
    newFeeData?.some((cat) => cat.amount === '');

  const hasChangesInFeeConfig =
    JSON.stringify(newFeeData) !== JSON.stringify(feeCategories);

  const handleEditClick = async (
    event: React.MouseEvent,
    memberId: string,
    groupId: string
  ) => {
    event.stopPropagation();

    const rowData = leadBranchGroupDetailsData.find(
      (row) => row.member.id === memberId && (row.group?.id || '') === groupId
    );
    if (!rowData) return;

    setIsEditDataLoading(true);
    setIsEditMode(true);
    setIsOpen(true);

    const branchOption = branchListOptions.find(
      (b) => b.id === rowData.branch.id
    );
    if (branchOption) setSelectedBranch(branchOption);
    setEditMemberId(rowData.member.id);

    if (!rowData.group) {
      setIsBranchOnlyEdit(true);
      await handleGetAllGroups(rowData.branch.id);
      setSelectedGroup(null);
      setGroupFormData(null);
      setIsEditDataLoading(false);
      return;
    }

    const res = await leadMemberFeeCollectionSettings({
      organizationId,
      branchId: rowData.branch.id,
      groupId: rowData.group.id,
      memberId: rowData.member.id
    });

    if ('data' in res && res.data) {
      const responseData = res.data as any;
      await handleGetAllGroups(rowData.branch.id);

      const groupOption = {
        id: rowData.group.id,
        value: rowData.group.name,
        label: rowData.group.name
      };
      setSelectedGroup(groupOption);

      const paymentSchedule = responseData.group_member?.payment_schedule;
      const formData = buildEditFormData(
        rowData.group.name,
        paymentSchedule,
        responseData.group_member
      );

      const memberFeeConfig =
        responseData.group_member?.fee_amount_configuration;
      const termFeeSplit =
        formData.isCustomTerm && formData.termsList?.length
          ? formData.termsList.find((t: TermsList) => t.feeCategoryData?.length)
              ?.feeCategoryData
          : null;

      if (
        formData.isCustomTerm &&
        formData.termsList?.length &&
        (memberFeeConfig?.length || termFeeSplit?.length)
      ) {
        const baseFeeConfig = memberFeeConfig || termFeeSplit || [];
        formData.termsList = formData.termsList.map((term: TermsList) => ({
          ...term,
          feeCategoryData:
            term.feeCategoryData && term.feeCategoryData.length > 0
              ? term.feeCategoryData
              : baseFeeConfig
        }));
        setFeeCategories(baseFeeConfig);
      } else if (memberFeeConfig?.length) {
        setFeeCategories(memberFeeConfig);
      } else {
        setFeeCategories([]);
      }

      setGroupFormData(formData);
      setStartDate(paymentSchedule?.start_date || '');
    } else if ('error' in res) {
      const error = res.error as ApiError;
      createErrorAlert(
        error.data?.message ||
          'Error in fetching member fee collection settings'
      );
      setIsOpen(false);
      setIsEditMode(false);
    }

    setIsEditDataLoading(false);
  };

  useEffect(() => {
    if (groupFormData && !isEditMode && groupFormData.isCustomTerm) {
      const formattedStartDate = formatISODate({
        date: new Date(),
        separator: '-',
        format: 'yyyy-mm-dd'
      });
      setStartDate(formattedStartDate);
      handleFormInputChange('startDate', formattedStartDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupFormData?.isCustomTerm]);

  useEffect(() => {
    if (groupFormData?.termsList && groupFormData.isCustomTerm) {
      const { updatedTermsList, totalAmount } = processTermsList(
        groupFormData.termsList
      );
      handleFormInputChange('amount', totalAmount.toString());
      updateTermsList(updatedTermsList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, groupFormData?.isCustomTerm]);

  useEffect(() => {
    if (leadName && leadMobileNumber) {
      fetchBranchGroupDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadName, leadMobileNumber, leadCountryCode, organizationId]);

  useEffect(() => {
    if (accountType.type === AccountType.ORGANIZATION) {
      handleGetAllBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedBranch?.id && !isEditMode) {
      setSelectedGroup(null);
      setGroupsList([]);
      setGroupFormData(null);
      handleGetAllGroups(selectedBranch.id as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  useEffect(() => {
    if (
      selectedBranch?.id &&
      selectedGroup?.id &&
      (!isEditMode || !groupFormData || isBranchOnlyEdit)
    ) {
      handleGetGroupById(
        selectedBranch.id as string,
        selectedGroup.id as string
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  const totalCount = leadBranchGroupDetailsData.length;

  const paginatedBranchGroupData = useMemo(() => {
    const startIndex = (page - 1) * PAGINATION_LIMIT;
    return leadBranchGroupDetailsData.slice(
      startIndex,
      startIndex + PAGINATION_LIMIT
    );
  }, [leadBranchGroupDetailsData, page]);

  return {
    organizationId,
    currencyConfig,
    leadName,
    leadCountryCode,
    leadMobileNumber,
    branchListOptions,
    groupListOptions,
    selectedBranch,
    setSelectedBranch,
    selectedGroup,
    setSelectedGroup,
    groupFormData,
    startDate,
    setStartDate,
    formError,
    setFormError,
    isOpen,
    isEditMode,
    isTableLoading,
    isGroupDetailsLoading,
    isEditDataLoading,
    isSubmitting,
    showDuplicateModal,
    leadBranchGroupDetailsData,
    paginatedBranchGroupData,
    totalCount,
    page,
    setPage,
    handleFormInputChange,
    handleAddModalOpen,
    handleModalClose,
    handleClearGroup,
    handleAddToBranchGroup,
    handleEditClick,
    handleDuplicateModalCancel,
    handleDuplicateModalProceed,
    onAmountChange,
    onAmountBlur,
    updateTermsList,
    editMemberId,
    isBranchOnlyEdit,
    isLeadAddToBranchLoading,
    isLeadAddToGroupLoading,
    isFeeConfigured,
    feeCategories,
    showFeeConfigModal,
    formattedFeeCategories,
    isFeeConfigSaveDisabled,
    hasChangesInFeeConfig,
    handleAmountClick,
    onCategoryAmountChange,
    handleFeeConfigSave,
    handleFeeConfigCancel
  };
};

export default useAddToBranchGroup;
